import { forwardRef, useCallback, useEffect, useImperativeHandle, useRef, useState } from "react";
import { DocxEditor } from "@eigenpal/docx-editor-react";

import { handleVariableChange2 } from "@/Components/WordProcessor/helpers";

import "@eigenpal/docx-editor-react/styles.css";

const DOCX_MIME_TYPE = "application/vnd.openxmlformats-officedocument.wordprocessingml.document";

const FaxTemplateEditor = forwardRef(function FaxTemplateEditor(
  {
    templateUrl,
    templateName,
    variables,
    loadingVariables,
    variablesError,
    loadVariables,
    saving,
    onSave,
    onError,
    onSuccess,
    onDirtyChange,
  },
  forwardedRef
) {
  const editorRef = useRef(null);
  const editorReadyRef = useRef(false);
  const [documentBuffer, setDocumentBuffer] = useState(null);
  const [loadingDocument, setLoadingDocument] = useState(false);
  const [documentError, setDocumentError] = useState("");
  const [dirty, setDirty] = useState(false);
  const [editorReady, setEditorReady] = useState(false);

  const handleEditorReady = () => {
    editorReadyRef.current = true;
    setEditorReady(true);
  };

  useEffect(() => {
    onDirtyChange(dirty);
  }, [dirty, onDirtyChange]);

  useEffect(() => {
    if (!dirty) return undefined;
    const warnBeforeUnload = (event) => {
      event.preventDefault();
      event.returnValue = "";
    };
    window.addEventListener("beforeunload", warnBeforeUnload);
    return () => window.removeEventListener("beforeunload", warnBeforeUnload);
  }, [dirty]);

  useEffect(() => {
    if (!templateUrl) {
      setDocumentBuffer(null);
      setDocumentError("");
      setDirty(false);
      setEditorReady(false);
      editorReadyRef.current = false;
      return undefined;
    }

    const controller = new AbortController();
    const loadDocument = async () => {
      setLoadingDocument(true);
      setDocumentError("");
      setDocumentBuffer(null);
      setDirty(false);
      setEditorReady(false);
      editorReadyRef.current = false;

      try {
        const response = await fetch(templateUrl, {
          cache: "no-store",
          signal: controller.signal,
        });
        if (!response.ok) throw new Error("The uploaded template could not be downloaded.");
        const buffer = await response.arrayBuffer();
        if (!buffer.byteLength) throw new Error("The uploaded template is empty.");
        setDocumentBuffer(buffer);
      } catch (error) {
        if (error.name === "AbortError") return;
        const message = error.message || "The uploaded template could not be opened.";
        setDocumentError(message);
        onError(message);
      } finally {
        if (!controller.signal.aborted) setLoadingDocument(false);
      }
    };

    loadDocument();
    return () => controller.abort();
  }, [onError, templateUrl]);

  const insertVariable = (placeholder) => {
    if (!editorReadyRef.current || saving) return;
    handleVariableChange2(placeholder, editorRef);
    setDirty(true);
  };

  const saveEditedTemplate = useCallback(
    async ({ silent = false } = {}) => {
      if (!editorRef.current || saving) return false;

      try {
        const content = await editorRef.current.save();
        if (!content?.byteLength) throw new Error("The edited template is empty.");

        const file = new File([content], templateName || "fax-cover-sheet.docx", {
          type: DOCX_MIME_TYPE,
        });
        const formData = new FormData();
        formData.append("cover_sheet_template", file);
        await onSave(formData);
        setDirty(false);
        if (!silent) return true;
        return true;
      } catch (error) {
        onError(error.message || "The edited fax cover sheet could not be saved.");
        return false;
      }
    },
    [onError, onSave, onSuccess, saving, templateName]
  );

  useImperativeHandle(
    forwardedRef,
    () => ({
      saveEditedTemplate,
    }),
    [saveEditedTemplate]
  );

  if (!templateUrl) {
    return (
      <div className="fax-template-editor-empty">
        <div className="fax-template-editor-empty-icon">W</div>
        <strong>Upload a cover sheet before editing</strong>
        <span>Return to the Upload tab and add a PDF or Word template to continue.</span>
      </div>
    );
  }

  return (
    <div className="fax-template-editor-layout">
      <section className="fax-template-editor-document" aria-label="Fax cover sheet editor">
        <div className="fax-template-editor-canvas">
          {loadingDocument ? (
            <div className="fax-template-editor-state" role="status">
              <span className="spinner-border spinner-border-sm" aria-hidden="true" />
              Opening cover sheet…
            </div>
          ) : documentError ? (
            <div className="fax-template-editor-state is-error" role="alert">
              <strong>Template could not be opened</strong>
              <span>{documentError}</span>
            </div>
          ) : documentBuffer ? (
            <DocxEditor
              key={templateUrl}
              ref={editorRef}
              documentBuffer={documentBuffer}
              mode="editing"
              documentName={templateName || "Fax cover sheet"}
              showToolbar
              showRuler={false}
              showZoomControl={false}
              onChange={() => {
                if (editorReadyRef.current) setDirty(true);
              }}
              onEditorViewReady={handleEditorReady}
              onFontsLoaded={handleEditorReady}
              style={{ height: "100%" }}
            />
          ) : null}
        </div>
      </section>

      <aside className="fax-template-variable-panel" aria-labelledby="fax-field-library-title">
        <span id="fax-field-library-title" className="fax-template-variable-library-title">
          Fax Variables
        </span>

        <div className="fax-template-variable-list">
          {loadingVariables ? (
            <div className="fax-template-editor-state" role="status">
              Loading fax variables…
            </div>
          ) : variablesError ? (
            <div className="fax-template-editor-state is-error" role="alert">
              <span>{variablesError.message}</span>
              <button type="button" onClick={() => loadVariables().catch(() => {})}>
                Retry
              </button>
            </div>
          ) : variables.length ? (
            <section className="fax-template-variable-group">
              <div className="fax-template-variable-grid">
                {variables.map((variable) => (
                  <button
                    type="button"
                    key={variable.name}
                    className="fax-template-variable-button height-25 font-weight-semibold cursor-pointer"
                    disabled={!editorReady || saving}
                    title={variable.description || variable.placeholder}
                    onClick={() => insertVariable(variable.placeholder)}
                  >
                    {variable.name.replaceAll("_", " ")}
                  </button>
                ))}
              </div>
            </section>
          ) : (
            <div className="fax-template-editor-state">No fax variables are available.</div>
          )}
        </div>

        <div className="fax-template-editor-actions">
          <button
            type="button"
            className={`btn height-25 ${dirty ? "btn-success" : "btn-secondary"}`}
            disabled={!documentBuffer || !dirty || saving}
            onClick={() => saveEditedTemplate()}
          >
            {saving ? "Saving…" : "Save Edited Template"}
          </button>
        </div>
      </aside>
    </div>
  );
});

export default FaxTemplateEditor;
